import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getReports } from "../services/api";
import { Gem, Users, ClipboardList, CheckCircle2 } from "lucide-react";

function Bar({ label, value, max, accent }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-vault-cream/90">{label}</span>
        <span className="text-vault-subtle font-medium">{value}</span>
      </div>
      <div className="h-2.5 rounded-full bg-vault-panel2 overflow-hidden">
        <div className={`h-full rounded-full ${accent ? "bg-vault-accent" : "bg-vault-subtle/50"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const Card = ({ children }) => <div className="rounded-3xl bg-vault-panel p-6">{children}</div>;

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { getReports().then(setData).catch(() => {}); }, []);

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="rounded-3xl bg-vault-panel p-6">
      <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-widest text-vault-accent">{label}</div><Icon className="h-5 w-5 text-vault-subtle" /></div>
      <div className="font-display text-4xl font-extrabold text-vault-cream mt-3">{value}</div>
    </div>
  );

  if (!data) return <div className="text-vault-panel">Loading reports\u2026</div>;

  const toArr = (obj, filterZero) => Object.entries(obj || {}).map(([label, value]) => ({ label, value })).filter((x) => !filterZero || x.value > 0);
  const byStatus = toArr(data.byStatus, true);
  const byType = toArr(data.byType);
  const byLoc = toArr(data.byLocation);
  const gemsByLoc = toArr(data.gemsByLocation);
  const m = (arr) => Math.max(1, ...arr.map((x) => x.value));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">{user.role}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">Reports & Analytics</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ClipboardList} label="Total Requests" value={data.totalRequests} />
        <Stat icon={CheckCircle2} label="Approved" value={data.approved} />
        <Stat icon={Users} label="Active Users" value={data.activeUsers} />
        <Stat icon={Gem} label="Gems Issued" value={data.gemsIssued} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-display text-lg font-bold text-vault-cream mb-5">Requests by Status</h3>
          <div className="space-y-4">{byStatus.length ? byStatus.map((x) => <Bar key={x.label} {...x} max={m(byStatus)} accent />) : <p className="text-sm text-vault-subtle">No data.</p>}</div>
        </Card>
        <Card>
          <h3 className="font-display text-lg font-bold text-vault-cream mb-5">Requests by Type</h3>
          <div className="space-y-4">{byType.map((x) => <Bar key={x.label} {...x} max={m(byType)} />)}</div>
        </Card>
        <Card>
          <h3 className="font-display text-lg font-bold text-vault-cream mb-5">Requests by Location</h3>
          <div className="space-y-4">{byLoc.map((x) => <Bar key={x.label} {...x} max={m(byLoc)} accent />)}</div>
        </Card>
        <Card>
          <h3 className="font-display text-lg font-bold text-vault-cream mb-5">Gems Balance by Location</h3>
          <div className="space-y-4">{gemsByLoc.map((x) => <Bar key={x.label} {...x} max={m(gemsByLoc)} />)}</div>
        </Card>
      </div>
    </div>
  );
}
